import { createAsyncThunk } from '@reduxjs/toolkit';
import { loginUser, signupUser, forgotPassword, resetPassword, getUserProfile, updateUserProfile, changePassword } from '../../services/authService';

// Login thunk
export const loginUserThunk = createAsyncThunk(
  'auth/loginUser',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await loginUser(credentials);
      

      // Transform the API response to match our Redux state structure
      const userData = {
        user: {
          firstName: response.user.firstName,
          lastName: response.user.lastName,
          profilePicture: response.user?.profilePicture?.url || null,
          phone: response.user?.phone || null,
          company: response.user?.company || null,
          id: response.user._id,
          email: response.user.email,
          tokens: response.user.tokens,
          apiKeys: response.apiKey,
          purchaseHistory: response.user.purchaseHistory,
        },
        role: response.user.role.toLowerCase(), // Convert "USER" to "user"
        token: response.token, // Use the first API key as token
        apiKeys: response.user.apiKeys?.[0]?.key || null,
      };

      return userData;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Signup thunk
export const signupUserThunk = createAsyncThunk(
  'auth/signupUser',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await signupUser(userData);

      const transformedData = {
        user: {
          firstName: response.user.firstName,
          lastName: response.user.lastName,
          profilePicture: response.user.profilePicture.url || null,
          phone: response.user.phone || null,
          company: response.user.company || null,
          id: response.user._id,
          email: response.user.email,
          tokens: response.user.tokens,
          apiKeys: response.apiKey,
          purchaseHistory: response.user.purchaseHistory,
        },
        role: response.user.role.toLowerCase(), // Convert "USER" to "user"
        token: response.token, // Use the first API key as token
        apiKeys: response.user.apiKeys?.[0]?.key || null,
      };

      return transformedData;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Forgot password thunk
export const forgotPasswordThunk = createAsyncThunk(
  'auth/forgotPassword',
  async (email, { rejectWithValue }) => {
    try {
      const response = await forgotPassword(email);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Reset password thunk
export const resetPasswordThunk = createAsyncThunk(
  'auth/resetPassword',
  async ({ token, newPassword }, { rejectWithValue }) => {
    try {
      const response = await resetPassword(token, newPassword);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Get user profile thunk
export const getUserProfileThunk = createAsyncThunk(
  'auth/getUserProfile',
  async (token, { rejectWithValue }) => {
    try {
      const response = await getUserProfile(token);

      const transformedData = {
        user: {
          id: response._id.$oid,
          email: response.email,
          tokens: response.tokens,
          apiKeys: response.apiKeys,
          purchaseHistory: response.purchaseHistory,
        },
        role: response.role.toLowerCase(),
      };

      return transformedData;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Update user profile thunk
export const updateUserProfileThunk = createAsyncThunk(
  'auth/updateUserProfile',
  async ({ token, profileData }, { rejectWithValue }) => {
    try {
      const response = await updateUserProfile(token, profileData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Change password thunk
export const changePasswordThunk = createAsyncThunk(
  'auth/changePassword',
  async ({ token, passwordData }, { rejectWithValue }) => {
    try {
      const response = await changePassword(token, passwordData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Helper functions for dummy mode
const isDemoCredentials = (credentials) => {
  const demoCredentials = [
    { email: "admin@nkenne.com", password: "admin123" },
    { email: "user@nkenne.com", password: "user123" }
  ];
  
  return demoCredentials.some(demo => 
    demo.email === credentials.email && demo.password === credentials.password
  );
};

const createDummyUserData = (credentials) => {
  const role = credentials.email === "admin@nkenne.com" ? "admin" : "user";
  
  return {
    user: {
      firstName: role === "admin" ? "Admin" : "John",
      lastName: role === "admin" ? "User" : "Doe",
      profilePicture: null,
      phone: "+1234567890",
      company: role === "admin" ? "Nkenne AI" : "Demo Company",
      id: role === "admin" ? "admin123" : "user123",
      email: credentials.email,
      tokens: 100,
      apiKeys: "demo-api-key-123",
      purchaseHistory: [],
    },
    role: role,
    token: `dummy-token-${role}-${Date.now()}`,
    apiKeys: `dummy-api-key-${role}`,
  };
};

const createDummySignupData = (userData) => {
  // Simulate a small delay for realistic behavior
  return new Promise((resolve) => {
    setTimeout(() => {
      const role = userData.email === "admin@nkenne.com" ? "admin" : "user";
      
      resolve({
        user: {
          firstName: userData.firstName,
          lastName: userData.lastName,
          profilePicture: null,
          phone: "+1234567890",
          company: role === "admin" ? "Nkenne AI" : "Demo Company",
          id: `demo-${role}-${Date.now()}`,
          email: userData.email,
          tokens: 100,
          apiKeys: "demo-api-key-123",
          purchaseHistory: [],
        },
        role: role,
        token: `dummy-token-${role}-${Date.now()}`,
        apiKeys: `dummy-api-key-${role}`,
      });
    }, 1000); // 1 second delay to simulate network request
  });
};
